import React, { useState } from 'react';

/**
 * A doksi-oldalak közös építőelemei.
 *
 * Ezeket használja minden doksi-oldal — ha egy elem kinézetét itt állítod,
 * az összes oldalon egyszerre változik.
 */

const CALLOUT_ICONS = {
  warning: <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" /></>,
  info:    <><circle cx="12" cy="12" r="9" /><path d="M12 16v-5M12 8h.01" /></>,
  success: <path d="M20 6L9 17l-5-5" />,
  danger:  <><circle cx="12" cy="12" r="9" /><path d="M15 9l-6 6M9 9l6 6" /></>,
};

function Callout({ type = 'warning', children }) {
  const cls = {
    warning: 'bg-amber-500/10 border-amber-500/25 text-amber-300',
    info:    'bg-sky-500/10 border-sky-500/25 text-sky-300',
    success: 'bg-emerald-500/10 border-emerald-500/25 text-emerald-300',
    danger:  'bg-red-500/10 border-red-500/25 text-red-300',
  }[type];

  return (
    <div className={`flex items-start gap-3 px-4 py-3.5 border rounded-lg text-sm my-5 ${cls}`}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round"
           className="w-4 h-4 flex-shrink-0 mt-0.5">
        {CALLOUT_ICONS[type]}
      </svg>
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

function Step({ n, title, children }) {
  return (
    <div className="relative flex gap-4 my-5 pl-0">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-brand-gradient border border-brand-400/25
                      flex items-center justify-center text-white text-xs font-bold mt-0.5
                      shadow-glow-sm">
        {n}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-white mb-1.5">{title}</p>
        <div className="text-gray-400 text-sm leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function CodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(children).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div className="relative group my-4">
      <pre className="bg-black/35 border border-white/[0.07] rounded-lg px-4 py-3.5 pr-16
                      text-sm font-mono text-brand-200 overflow-x-auto whitespace-pre-wrap break-all">
        {children}
      </pre>
      <button
        onClick={copy}
        className={`absolute top-2.5 right-2.5 text-xs px-2 py-1 rounded-md border
                    transition-all duration-200 ${
          copied
            ? 'opacity-100 text-emerald-300 bg-emerald-500/10 border-emerald-500/25'
            : 'opacity-0 group-hover:opacity-100 text-gray-400 bg-white/[0.06] border-white/[0.08] hover:text-white'
        }`}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/**
 * KEPHELY a doksiban.
 *
 * A `name` a VART FAJLNEV a `public/docs/shots/` mappaban. A kepet NEM kell
 * bekotni: ha a fajl ott van, megjelenik; ha nincs, egy szaggatott keretes
 * helyorzo all a helyen, ES KIIRJA, milyen nevu fajlt var. Vagyis a kepek
 * potlasa tiszta fajlmasolas — a JSX-hez nem kell hozzanyulni.
 *
 *   <Shot tab="AUTOPILOT › Play" caption="The Play tab during a run"
 *         name="autopilot-01-play-tab.png"
 *         shoot="A Play fül futó menet közben — látszik a zöld RUNNING sor." />
 *
 * A `caption` ANGOL, mert a nyilvános oldalon a kép alatt jelenik meg. A
 * `shoot` MAGYAR utasítás a képkészítőnek (melyik fül, milyen állapot, mi
 * látsszon) — a komponens NEM rajzolja ki, csak a `frontend/DOCS-KEPEK.md`
 * lista generálása olvassa (`scripts/docs-shots.js`).
 * A fájlnév `<oldal-id>-NN-<rövid-név>.png`, így két oldal képei nem ütköznek.
 *
 * A `src` a kivetel: ha valamiert mashonnan jon a kep, azt is meg lehet adni,
 * es az eloz a `name`-nel.
 */
function Shot({ tab, caption, name, src, alt }) {
  const [failed, setFailed] = useState(false);
  const url = src || (name ? `/docs/shots/${name}` : null);

  if (url && !failed) {
    return (
      <figure className="my-6">
        {/* A KEP KATTINTHATO. Egy menu-kepernyokep a doksi oszlopszelessegen
            apro: a fulek szovege alig olvashato. A link uj lapon nyitja meg
            teljes meretben — nem kell hozza modal, es a jobb gombos "kep
            mentese" is mukodik.

            max-w-full, NEM w-full: egy kis kivagast (pl. egy kartya vagy a
            kepernyos szamlalo) nem szabad a kartya szelessegere nyujtani, mert
            elmosodik. A nagy kepek tovabbra is beleferre zsugorodnak. */}
        <a href={url} target="_blank" rel="noopener noreferrer" className="block group">
          <img
            src={url}
            alt={alt || caption || tab}
            onError={() => setFailed(true)}
            className="max-w-full h-auto mx-auto block rounded-lg border border-white/[0.08]
                       shadow-lg transition-colors duration-200 group-hover:border-brand-400/40"
          />
        </a>
        {(caption || tab) && (
          <figcaption className="mt-2.5 flex items-center gap-2 text-xs text-gray-500">
            {tab && (
              <span className="px-2 py-0.5 rounded border border-brand-400/25 bg-brand-500/10
                               text-brand-300 font-mono text-[10px] tracking-wide whitespace-nowrap">
                {tab}
              </span>
            )}
            <span className="leading-relaxed">{caption}</span>
            <span className="ml-auto hidden sm:inline text-[10px] text-gray-600 whitespace-nowrap">
              click to enlarge
            </span>
          </figcaption>
        )}
      </figure>
    );
  }

  return (
    <div className="my-6 rounded-lg border border-dashed border-brand-400/30 bg-brand-500/[0.04]
                    px-5 py-6 flex items-start gap-4">
      <div className="w-9 h-9 rounded-lg bg-brand-500/12 border border-brand-400/20
                      flex items-center justify-center text-brand-300 flex-shrink-0">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
             strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
          <path d="M3 8.5A2.5 2.5 0 015.5 6h1.2a1.5 1.5 0 001.3-.75l.6-1A1.5 1.5 0 019.9 3.5h4.2a1.5 1.5 0 011.3.75l.6 1A1.5 1.5 0 0017.3 6h1.2A2.5 2.5 0 0121 8.5v9A2.5 2.5 0 0118.5 20h-13A2.5 2.5 0 013 17.5z" />
          <circle cx="12" cy="13" r="3.5" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <span className="text-[10px] font-semibold tracking-wider uppercase text-gray-500">
            Screenshot
          </span>
          {tab && (
            <span className="px-2 py-0.5 rounded border border-brand-400/25 bg-brand-500/10
                             text-brand-300 font-mono text-[10px] tracking-wide">
              {tab}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-400 leading-relaxed">{caption}</p>
        {name && (
          <p className="mt-2 text-xs text-gray-600 font-mono break-all">
            public/docs/shots/<span className="text-gray-500">{name}</span>
          </p>
        )}
      </div>
    </div>
  );
}

function DocHeading({ children }) {
  return (
    <h2 className="font-display text-xl font-bold text-white tracking-tight
                   mt-12 mb-4 pb-3 border-b border-white/[0.07] flex items-center gap-2.5">
      <span className="w-1 h-5 rounded-full inline-block flex-shrink-0
                       bg-gradient-to-b from-brand-300 to-brand-600" />
      {children}
    </h2>
  );
}

function QuickCard({ icon, title, desc, onClick, external }) {
  const Tag = external ? 'a' : 'button';
  const extraProps = external
    ? { href: external, target: '_blank', rel: 'noopener noreferrer' }
    : { onClick };
  return (
    <Tag
      {...extraProps}
      className="card card-interactive p-5 text-left group block"
    >
      <div className="w-9 h-9 rounded-lg bg-brand-500/12 border border-brand-400/20
                      flex items-center justify-center text-brand-300 mb-3.5
                      group-hover:bg-brand-500/20 group-hover:text-brand-200
                      transition-colors duration-300">
        {icon}
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <p className="font-semibold text-white text-sm">{title}</p>
        {external && (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round"
               className="w-3 h-3 text-gray-600 ml-auto group-hover:text-gray-400 transition-colors">
            <path d="M7 17L17 7M7 7h10v10" />
          </svg>
        )}
      </div>
      <p className="text-xs text-gray-500 leading-relaxed">{desc}</p>
    </Tag>
  );
}

function ReqRow({ label, value, ok = true }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3
                    border-b border-white/[0.05] last:border-0 text-sm">
      <span className="text-gray-500">{label}</span>
      <div className="flex items-center gap-2">
        <span className="text-gray-200 text-right">{value}</span>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
             strokeLinecap="round" strokeLinejoin="round"
             className={`w-3.5 h-3.5 flex-shrink-0 ${ok ? 'text-emerald-400' : 'text-amber-400'}`}>
          {ok
            ? <path d="M20 6L9 17l-5-5" />
            : <><path d="M12 9v4M12 17h.01" /><path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" /></>}
        </svg>
      </div>
    </div>
  );
}

function DocTable({ headers, rows }) {
  return (
    <div className="card overflow-hidden mb-6 scroll-x">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-white/[0.06] bg-white/[0.03]">
            {headers.map(h => (
              <th key={h} className="text-left px-5 py-3 table-head whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/[0.05] last:border-0
                                   hover:bg-white/[0.02] transition-colors">
              {row.map((cell, j) => (
                <td key={j} className="px-5 py-3 text-gray-400 align-top leading-relaxed">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ICONS = {
  rocket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
    </svg>
  ),
  key: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="7.5" cy="15.5" r="4.5" />
      <path d="M21 2l-9.6 9.6M15.5 7.5l3 3" />
    </svg>
  ),
  features: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4z" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4z" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M10.3 3.86l-8.18 14a1.5 1.5 0 001.3 2.14h16.16a1.5 1.5 0 001.3-2.14l-8.18-14a1.5 1.5 0 00-2.6 0z" />
      <path d="M12 9v4M12 17h.01" />
    </svg>
  ),
  headset: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M4 13a8 8 0 0116 0" />
      <rect x="2.5" y="13" width="4" height="6" rx="1.5" />
      <rect x="17.5" y="13" width="4" height="6" rx="1.5" />
      <path d="M19.5 19v1a3 3 0 01-3 3h-3" />
    </svg>
  ),
  discord: (
    <svg viewBox="0 0 24 24" fill="#5865F2" width="20" height="20" style={{ flexShrink: 0 }}>
      <path fillRule="evenodd" clipRule="evenodd" d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942.0209-.0414.001-.091-.0417-.1057a13.2019 13.2019 0 01-1.8724-.8936.0767.0767 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z" />
    </svg>
  ),
};

export { Callout, Step, CodeBlock, DocHeading, QuickCard, ReqRow, DocTable, Shot, ICONS };
